import {
  Affix,
  Button,
  Divider,
  Form,
  Space,
  Col,
  Row,
  Typography,
  Empty,
  InputNumber
} from 'antd'

import React, { useEffect, useState } from 'react'

import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons'
import { DateTime } from 'luxon'
import PlagarismTag from '../../components/PlagarismTag'
import GradingQuestionList from './components/GradingQuestionList'
import AutoGradingTag from '../../components/AutoGradingTag'
import SubmissionTimeTag from '../../components/SubmissionTimeTag'

import Styled from 'styled-components'
import FileDisplay from './components/FileDispaly'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useParams } from 'react-router'
import {
  getAllSubmissions,
  gradeQuestions
} from '../../reducers/submissionsReducer'
import Spinner from '../../components/Spinner'
import { useNavigate } from 'react-router-dom'

const { Text, Title } = Typography

const Container = Styled.div`
    background-color: #fafafa;
    width: 95%;
    margin: 0 auto;
    border-radius: 10px;
    padding: 20px 20px;
`

const Grader = () => {
  const dispatch = useDispatch()

  const { courseId, assessmentId } = useParams()

  const data = useSelector((state) => state.submissions.data)
  const loading = useSelector((state) => state.submissions.loading)
  const { assessment, submissions } = data || {}

  useEffect(() => {
    dispatch(getAllSubmissions(courseId, assessmentId))
  }, [courseId, assessmentId])

  if (loading) return <Spinner size="large" />

  if (!submissions || (Array.isArray(submissions) && submissions.length === 0))
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />

  return <GradingPage submissions={submissions} assessment={assessment} />
}

const getFileType = (url) => {
  if (!url) return 'unknown'
  const extension = url.split('.').pop().toLowerCase()
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) {
    return 'image'
  }
  if (extension === 'pdf') {
    return 'pdf'
  }
  return 'other'
}

const FileView = ({ files }) => {
  const [selectedFile, setSelectedFile] = useState(null)


  const selectFile = (fileObject) => {
    setSelectedFile({
      url: fileObject.url,
      type: getFileType(fileObject.url),
      name: fileObject.name
    })
  }

  useEffect(() => {
    if (files && files.length > 0) {
      const firstDisplayableFile = files.find((f) => {
        const type = getFileType(f.url)
        return type === 'pdf' || type === 'image'
      })

      if (firstDisplayableFile) {
        setSelectedFile({
          url: firstDisplayableFile.url,
          type: getFileType(firstDisplayableFile.url),
          name: firstDisplayableFile.name
        })
      } else {
        setSelectedFile(null)
      }
    } else {
      setSelectedFile(null)
    }
  }, [files])

  return (
    <>
      <FileDisplay files={files} handleClick={selectFile} />

      <div
        style={{
          width: '100%',
          height: '680px',
          marginTop: '16px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#f0f2f5',
          overflow: 'hidden',
          border: selectedFile ? '1px solid #d9d9d9' : 'none'
        }}
      >
        {!selectedFile && files && files.length > 0 && (
          <p>No displayable files (PDF or image) found in the list.</p>
        )}
        {!selectedFile && (!files || files.length === 0) && (
          <p>No files to display.</p>
        )}
        {selectedFile && selectedFile.type === 'pdf' && (
          <embed
            title={selectedFile.name || 'PDF Document'}
            src={selectedFile.url}
            type="application/pdf"
            style={{ width: '100%', height: '100%', border: 'none' }}
            frameBorder="0"
          ></embed>
        )}
        {selectedFile && selectedFile.type === 'image' && (
          <img
            src={selectedFile.url}
            alt={selectedFile.name || 'Selected Image'}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              display: 'block'
            }}
          />
        )}
        {selectedFile && selectedFile.type === 'other' && (
          <p>
            This file type cannot be displayed directly. Please download to
            view:{' '}
            <a
              href={selectedFile.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {selectedFile.name}
            </a>
          </p>
        )}
      </div>
    </>
  )
}

const GradingPage = (props) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { state } = useLocation()
  const { courseId, assessmentId } = useParams()
  const { submissions, assessment } = props

  const [form] = Form.useForm()

  const [selectedIndex, setSelectedIndex] = useState(state?.index || 0)

  useEffect(() => {
    form.resetFields()
  }, [selectedIndex])

  const calculateTotalScore = (answers) => {
    let totalScore = 0
    for (const answer of answers) {
      totalScore += answer?.score || 0
    }
    return totalScore
  }

  const handleScoreCalculation = (changedValues, allValues) => {
    const answers = allValues?.answers
    if (
      answers &&
      answers.length !== 0 &&
      assessment.questionsType === 'online'
    ) {
      let totalScore = calculateTotalScore(answers)
      form.setFieldsValue({ score: totalScore })
    }
  }

  const handleSubmit = (submission) => {
    dispatch(
      gradeQuestions(
        courseId,
        assessmentId,
        submissions[selectedIndex].student._id,
        submission
      )
    ).then(() => {
      if (submissions.length - 1 !== selectedIndex)
        setSelectedIndex(selectedIndex + 1)
    })
  }

  const getInitialScore = () => {
    if (submissions[selectedIndex].score)
      return submissions[selectedIndex].score
    else return calculateTotalScore(submissions[selectedIndex].answers)
  }

  const getSubmissionStatus = (submission) => {
    if (!submission?.submittedAt) {
      return { code: 'notSubmitted', message: 'Not submitted' }
    }

    const submissionTime = DateTime.fromISO(submission.submittedAt)
    const dueDate = DateTime.fromISO(submission.assessment.dueDate)

    if (submissionTime <= dueDate) {
      return { code: 'onTime', message: 'Submitted on time' }
    } else {
      return { code: 'late', message: 'Submitted late' }
    }
  }

  return (
    <Form
      onValuesChange={handleScoreCalculation}
      form={form}
      initialValues={{
        score: getInitialScore()
      }}
      onFinish={handleSubmit}
      layout="vertical"
    >
      <Row gutter={[16, 16]}>
        <Col span={18}>
          <Container>
            <Title level={3}>
              {assessment.title} <Text type="secondary">submission</Text>{' '}
              {selectedIndex + 1} <Text type="secondary">out of</Text>{' '}
              {submissions.length}
            </Title>
            <Divider />
            {assessment.questionsType === 'online' && (
              <GradingQuestionList
                form={form}
                answers={submissions[selectedIndex].answers}
                submissionId={submissions[selectedIndex].id}
              />
            )}
            {assessment.submissionType === 'written' && (
              <FileView files={submissions[selectedIndex].files} />
            )}
          </Container>
        </Col>

        <Col span={6}>
          <Affix offsetTop={10}>
            <div
              style={{
                backgroundColor: 'white',
                padding: '20px 20px',
                borderRadius: '10px'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Space>
                  <Button
                    type="text"
                    icon={<ArrowLeftOutlined />}
                    onClick={() => setSelectedIndex(selectedIndex - 1)}
                    disabled={selectedIndex === 0}
                  ></Button>
                  <Text>{submissions[selectedIndex].student.name}</Text>
                  <Button
                    type="text"
                    icon={<ArrowRightOutlined />}
                    onClick={() => setSelectedIndex(selectedIndex + 1)}
                    disabled={submissions.length - 1 === selectedIndex}
                  ></Button>
                </Space>
              </div>
              <Divider />

              <Space size="large" direction="vertical">
                <Text>
                  Submission Time:{' '}
                  {DateTime.fromISO(
                    submissions[selectedIndex].submittedAt
                  ).toLocaleString(DateTime.DATETIME_MED)}
                </Text>
                {assessment.submissionType === 'written' && (
                  <Space>
                    <Text>Plagarism Degree: </Text>
                    <PlagarismTag
                      status={submissions[selectedIndex].plagarismStatus}
                    />
                  </Space>
                )}
                {assessment?.questionsType === 'online' && (
                  <Space>
                    <Text>AutoGrading Status: </Text>
                    <AutoGradingTag
                      status={submissions[selectedIndex]?.autoGradingStatus}
                    />
                  </Space>
                )}
                {assessment?.type === 'Assignment' && (
                  <Space>
                    <Text>Submit Status: </Text>
                    <SubmissionTimeTag
                      status={getSubmissionStatus(submissions[selectedIndex])}
                    />
                  </Space>
                )}
                {assessment.type === 'Exam' && (
                  <Text>
                    Number of Joins:{' '}
                    {submissions[selectedIndex].numberOfExamJoins}
                  </Text>
                )}
                <Form.Item
                  initialValue={submissions[selectedIndex].score}
                  label={`Total Score out of ${assessment.maxScore}:`}
                  name="score"
                  preserve={false}
                  rules={
                    assessment.submissionType === 'written'
                      ? [{ required: true }]
                      : []
                  }
                >
                  <InputNumber
                    disabled={assessment.submissionType === 'online'}
                    style={{ width: '30%' }}
                    min={0}
                    max={assessment.maxScore}
                  ></InputNumber>
                </Form.Item>
                <Text> </Text>
              </Space>
              <Space style={{ marginTop: '26px' }}>
                <Button type="primary" onClick={() => form.submit()}>
                  Grade
                </Button>
                <Button
                  onClick={() =>
                    navigate(
                      `/app/course/${courseId}/${assessment.type.toLowerCase()}s`
                    )
                  }
                >
                  Cancel
                </Button>
              </Space>
            </div>
          </Affix>
        </Col>
      </Row>
    </Form>
  )
}

export default Grader
